import EditIcon from "@mui/icons-material/Edit";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Fab,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import RowElement from "components/RowElement";
import { hasUserPermission } from "helpers/ClaimHelpers";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import SkuCertificatesList from "pages/SkusPage/SkuCertificatesList";
import SkuRuleSection from "pages/SkusPage/SkuContentRules/SkuRuleSection";
import SkuEvaluationsList from "pages/SkusPage/SkuEvaluationsList";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  OrganizationAttributeTypeRequest,
  ResponseOfAdvancedPreparation,
  ResponseOfCorporateEntity,
  ResponseOfOrganizationDetails,
  ResponseOfProduct,
  ResponseOfProductVersion,
  ResponseOfProductYear,
  ResponseOfSkuAvailability,
  ResponseOfSkuFormat,
  ResponseOfSkus,
  ResponseOfSkuStatus,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { ClaimPermission, ClaimResource, ParentClaim } from "types/claimTypes";
import { AttributeDisplayTypes, FilterPages } from "types/common-types";
import {
  convertDateTimeTo12hrFormat,
  dateToStandardFormat,
} from "utils/dateUtils";
import SkuDeliveryMethods from "./SkuDeliveryMethods";
import SkuAttributesList from "./SkuDetailTables/SkuAttributesList";
import SkuKnowledgeLevels from "./SkuDetailTables/SkuKnowledgeLevels";
import SkuLearningLevels from "./SkuDetailTables/SkuLearningLevels";
import SkuLearningObjectivesList from "./SkuDetailTables/SkuLearningObjectivesList";
import SkuMajorTopicsList from "./SkuDetailTables/SkuMajorTopicsList";
import SkuProgressionLogic from "./SkuDetailTables/SkuProgressionLogic";
import SkuRelationships from "./SkuDetailTables/SkuRelationships";
import SkuResources from "./SkuDetailTables/SkuResources";
import SkuStudyFields from "./SkuDetailTables/SkuStudyFields";
import SkuHandouts from "./SkuHandouts/index";
import SkuLinksTable from "./SkuLinks/SkuLinksTable";
import SkuQualifications from "./SkuQualifications";
import { SkuPreview } from "./SkuPreview";
import InternalTags from "pages/SkusPage/InternalTags/InternalTags";
import SkuPageLayout from "pages/SkusPage/SkuSummarySidebar/SkuPageLayout";
import SkuPublishCard from "pages/SkusPage/SkuSummarySidebar/SkuPublishCard";
import {
  isFieldVisible,
  SkuFieldKey,
} from "./SkuDetails/skuFieldVisibilityMap";

const styles = {
  rowContent: {
    float: "right",
  },
  rowTitle: {
    float: "left",
    fontWeight: 600,
  },
  accordionDetails: { display: "flex", flexDirection: "column" },
  accordionSummary: { margin: 0 },
  noDecoration: {
    textDecoration: "none",
  },
};

const SkuFormDetails = () => {
  const location = useLocation();
  const { formatMessage: t } = useIntl();
  const {
    skuEditUrl,
    skusUrl,
    skuRegistrationsUrl,
    eventsUrl,
    skuService,
    productService,
    catalogService,
    organizationService,
    attributeService,
  } = useGlobalContext();

  const editSkuAllowed = useMemo(
    () =>
      hasUserPermission(
        ParentClaim.Content,
        ClaimResource.Skus,
        ClaimPermission.Edit,
      ),
    [],
  );

  const [previewOpen, setPreviewOpen] = useState(false);

  const { id: skuId } = useParams<{
    id: string;
    productId: string;
  }>();

  useEffect(() => {
    const previousPage = location.state;
    if (previousPage === FilterPages.SkuQualificationList) {
      persistanceStore.update((s) => {
        s.professionId = undefined;
        s.qualificationId = undefined;
        s.stateId = undefined;
        s.creditSubmissionExpirationDateFrom = new Date();
        s.creditSubmissionExpirationDateTo = undefined;
      });
    }
  }, [skuId, location.state]);

  const skuResponse = useStrictQuery<ResponseOfSkus>(
    ["skuById", skuId],
    () => skuService.getSkuById(skuId!),
    {
      enabled: !!skuId,
    },
  );

  const sku = skuResponse.data?.data;
  useTitle(sku?.sku || t({ id: "skuFormDetailsTitle" }));

  const {
    data: productData,
    isLoading: isLoadingProduct,
  } = useQuery<ResponseOfProduct>(
    ["product", skuId],
    () => productService.getProductById(sku?.productId!),
    { enabled: !!skuId && !!sku && !!sku.productId },
  );

  const {
    data: productYearData,
    isLoading: isLoadingProductYear,
  } = useQuery<ResponseOfProductYear>(
    ["productYear", skuId],
    () => productService.getProductYearById(sku?.productYearId!),
    { enabled: !!skuId && !!sku && !!sku.productYearId },
  );

  const {
    data: skuFormatData,
    isLoading: isLoadingSkuFormat,
  } = useQuery<ResponseOfSkuFormat>(
    ["skuFormat", skuId],
    () => skuService.getSkuFormatById(sku?.skuFormatId!),
    { enabled: !!skuId && !!sku && !!sku.skuFormatId },
  );

  const {
    data: skuAvailabilityData,
    isLoading: isLoadingSkuAvailability,
  } = useQuery<ResponseOfSkuAvailability>(
    ["skuAvailability", skuId],
    () => skuService.getSkuAvailabilityById(sku?.skuAvailabilityId!),
    { enabled: !!skuId && !!sku && !!sku.skuAvailabilityId },
  );

  const {
    data: productVersionData,
    isLoading: isLoadingProductVersion,
  } = useQuery<ResponseOfProductVersion>(
    ["productVersion", skuId],
    () => productService.getProductVersionById(sku?.productVersionId!),
    { enabled: !!skuId && !!sku && !!sku.productVersionId },
  );

  const { data: skuStatusData } = useQuery<ResponseOfSkuStatus>(
    ["skuService.getSkuStatusById", skuId, sku?.skuStatusId],
    () => skuService.getSkuStatusById(sku?.skuStatusId!),
    { enabled: !!skuId && !!sku && !!sku.skuStatusId },
  );

  const {
    data: advancedPreparationData,
    isLoading: isLoadingAdvancedPreparation,
  } = useQuery<ResponseOfAdvancedPreparation>(
    ["advancedPreparation", skuId],
    () => skuService.getAdvancedPreparationById(sku?.advancePreparationId!),
    { enabled: !!skuId && !!sku && !!sku?.advancePreparationId },
  );

  const {
    data: corporateEntityData,
    isLoading: isLoadingCorporateEntity,
  } = useQuery<ResponseOfCorporateEntity>(
    ["corporateEntity", skuId],
    () => catalogService.getCorporateEntityById(sku?.corporateEntityId!),
    { enabled: !!skuId && !!sku && !!sku?.corporateEntityId },
  );

  const {
    data: ownerOrganizationData,
    isLoading: isLoadingownerOrganization,
  } = useQuery<ResponseOfOrganizationDetails>(
    ["organization", sku?.ownerOrganizationId],
    () => organizationService.getOrganization(sku?.ownerOrganizationId!),
    { enabled: !!skuId && !!sku && !!sku?.ownerOrganizationId },
  );

  const request = OrganizationAttributeTypeRequest.fromJS({
    sortField: "name",
    organizationId: sku?.ownerOrganizationId,
    parameters: {
      attributeDisplayTypeId: AttributeDisplayTypes.Accordion,
    },
  });

  const {
    data: organizationAttributeTypesData,
    isLoading: isLoadingOrganizationAttributeTypes,
  } = useQuery(
    ["getOrganizationAttributeTypes", request],
    () => attributeService.getOrganizationAttributeTypes(request),
    { enabled: skuResponse.isFetched && !!sku },
  );

  const eventStartDate = convertDateTimeTo12hrFormat(sku?.eventStartDate);
  const eventEndDate = convertDateTimeTo12hrFormat(sku?.eventEndDate);

  const productTypeEnum = productData?.data?.productType?.enumValue;
  const isVisible = (key: SkuFieldKey) =>
    isFieldVisible(productTypeEnum, key, {
      skuAvailabilityId: sku?.skuAvailabilityId,
    });

  const detailsRows = [
    isVisible("ownerOrganizationId") &&
      RowElement(
        "ownerOrganization",
        ownerOrganizationData?.data?.name!,
        isLoadingownerOrganization,
      ),
    isVisible("productId") &&
      RowElement(
        "skuFormProductLabel",
        productData?.data?.computedName!,
        isLoadingProduct,
      ),
    isVisible("productYearId") &&
      RowElement(
        "skuFormProductYearLabel",
        productYearData?.data?.name!,
        isLoadingProductYear,
      ),
    isVisible("productVersionId") &&
      RowElement(
        "skuFormProductVersionLabel",
        productVersionData?.data?.name!,
        isLoadingProductVersion,
      ),
    isVisible("skuFormatId") &&
      RowElement(
        "skuFormSkuFormatLabel",
        skuFormatData?.data?.name!,
        isLoadingSkuFormat,
      ),
    isVisible("skuAvailabilityId") &&
      RowElement(
        "skuAvailabilityLabel",
        skuAvailabilityData?.data?.name!,
        isLoadingSkuAvailability,
      ),
    RowElement("skuFormLatestVersionSkuIdLabel", sku?.latestVersionSkuId!),
    isVisible("title") && RowElement("skuFormTitleLabel", sku?.title!),
    isVisible("description") &&
      RowElement(
        "skuFormDescriptionLabel",
        sku?.description!,
        false,
        sku?.description ? (
          <ResponsiveIframe
            srcDoc={sku.description}
            title="skuFormDescription"
            containerStyles={{ maxHeight: 200 }}
          />
        ) : undefined,
      ),
    isVisible("price") && RowElement("skuFormPriceLabel", sku?.price!),
    isVisible("memberPrice") &&
      RowElement("skuFormMemberPriceLabel", sku?.memberPrice!),
    isVisible("credits") && RowElement("skuFormCreditsLabel", sku?.credits!),
    isVisible("creditLimit") &&
      RowElement("skuFormCreditLimitLabel", sku?.creditLimit!),
    isVisible("sequence") && RowElement("skuFormSequenceLabel", sku?.sequence!),
    isVisible("releaseDate") &&
      RowElement("skuFormReleaseDateLabel", sku?.releaseDate!),
    isVisible("offlineDate") &&
      RowElement("skuFormOfflineDateLabel", sku?.offlineDate!),
    isVisible("creditSubmissionExpirationDate") &&
      RowElement(
        "skuFormCreditSubmissionExpirationDateLabel",
        sku?.creditSubmissionExpirationDate!,
      ),
    isVisible("expirationDate") &&
      RowElement("skuFormExpirationDateLabel", sku?.expirationDate!),
    isVisible("eventStartDate") &&
      RowElement(
        "skuFormEventStartDateLabel",
        eventStartDate ? dateToStandardFormat(eventStartDate) : "",
      ),
    isVisible("eventEndDate") &&
      RowElement(
        "skuFormEventEndDateLabel",
        eventEndDate ? dateToStandardFormat(eventEndDate) : "",
      ),
    isVisible("advancePreparationId") &&
      RowElement(
        "skuFormAdvancedPreparationLabel",
        advancedPreparationData?.data?.value!,
        isLoadingAdvancedPreparation,
      ),
    isVisible("corporateEntityId") &&
      RowElement(
        "skuFormCorporateEntityLabel",
        corporateEntityData?.data?.name!,
        isLoadingCorporateEntity,
      ),
    isVisible("hasChild") && {
      title: t({ id: "skuFormHasChildLabel" }),
      component: sku?.productId && (
        <Checkbox sx={styles.rowContent} checked={sku.hasChild}></Checkbox>
      ),
    },
  ].filter((r): r is { title: string; component: any; type?: any } =>
    r !== false && !!r && !!(r as any).component,
  );

  const propertiesRows = [
    isVisible("skuRelationships") && {
      title: t({ id: "skuRelationshipsTitle" }),
      component: <SkuRelationships skuId={skuId!} sku={sku} />,
    },
    isVisible("skuLinks") && {
      title: t({ id: "skuLinks" }),
      component: (
        <SkuLinksTable
          skuId={skuId!}
        />
      ),
    },
    isVisible("courseProgressionLogic") && {
      title: t({ id: "courseProgressionLogicTitle" }),
      component: <SkuProgressionLogic skuId={skuId!} />,
    },
    isVisible("knowledgeLevels") && {
      title: t({ id: "skuKnowledgeLevelLabel" }),
      component: <SkuKnowledgeLevels skuId={skuId!} />,
    },
    isVisible("learningLevels") && {
      title: t({ id: "skuLearningLevelLabel" }),
      component: <SkuLearningLevels skuId={skuId!} />,
    },
    isVisible("faculty") && {
      title: t({ id: "skuFacultyTitle" }),
      component: <SkuResources skuId={skuId!} />,
    },
    isVisible("studyFields") && {
      title: t({ id: "skuFormStudyFieldTitle" }),
      component: <SkuStudyFields skuId={skuId!} />,
    },
    isVisible("majorTopics") && {
      title: t({ id: "skuFormMajorTopicTitle" }),
      component: <SkuMajorTopicsList skuId={skuId!} sku={sku} />,
    },
    isVisible("learningObjectives") && {
      title: t({ id: "skuFormLearningObjectiveTitle" }),
      component: <SkuLearningObjectivesList skuId={skuId!} sku={sku} />,
    },
    isVisible("qualifications") && {
      title: t({ id: "accreditationBodies" }),
      component: <>{sku && <SkuQualifications isReadonly sku={sku} />}</>,
    },
    isVisible("courseHandouts") && {
      title: t({ id: "courseHandouts" }),
      component: <SkuHandouts sku={sku!} isReadonly />,
    },
    isVisible("contentDelivery") && {
      title: t({ id: "skuDeliveryMethodTitle" }),
      component: <SkuDeliveryMethods sku={sku!} isReadonly />,
    },
  ].filter(
    (r): r is { title: string; component: JSX.Element } => r !== false,
  );

  const attributesRows = [
    ...(organizationAttributeTypesData?.data
      ?.sort((a, b) => (a.sequence! > b.sequence! ? 1 : -1))
      .map((oat) => ({
        title: oat.displayName,
        component: (
          <SkuAttributesList
            skuId={skuId!}
            organizationAttributeTypeId={oat.id}
          />
        ),
      })) ?? []),
    {
      title: t({ id: "otherLabel" }),
      component: <SkuAttributesList skuId={skuId!} />,
    },
  ];

  if (skuResponse.isLoading) return <Loader />;

  return (
    <div>
      <Box mb={3}>
        <RedirectBackButton
          redirectRoute={location.state?.backToEvents ? eventsUrl() : skusUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <Box my={3} display="flex" justifyContent="center">
        {editSkuAllowed && (
          <>
            <Link to={skuEditUrl(skuId)} style={styles.noDecoration}>
              <Fab color="primary">
                <EditIcon />
              </Fab>
            </Link>
            <Box ml={4} />
          </>
        )}
        <Fab color="primary" onClick={() => setPreviewOpen(true)}>
          <Tooltip title="Preview Course Details">
            <PreviewIcon />
          </Tooltip>
        </Fab>
        <Box ml={4} />
        <Link to={skuRegistrationsUrl(skuId)} style={styles.noDecoration}>
          <Fab color="primary">
            <Tooltip title={<T id="registrations" />}>
              <EmojiPeopleIcon />
            </Tooltip>
          </Fab>
        </Link>
      </Box>

      <h1>{sku?.sku!}</h1>

      <h2>
        <T id="detailsLabel" />
      </h2>
      <SkuPageLayout
        sidebar={
          <Stack spacing={3}>
            <SkuPublishCard
              skuStatusId={sku?.skuStatusId}
              skuStatusName={skuStatusData?.data?.name}
              modifiedOn={sku?.modifiedOn}
              modifiedBy={sku?.modifiedBy}
            />
            {skuId && <InternalTags skuId={skuId} isReadonly />}
          </Stack>
        }
      >
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {detailsRows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <Box component="span" sx={styles.rowTitle}>
                      {title}
                    </Box>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {isVisible("evaluationsSection") && (
          <Box my={4}>
            <h2>
              <T id="evaluationsLabel" />
            </h2>
            {sku && sku.id && <SkuEvaluationsList skuId={sku.id} />}
          </Box>
        )}

        {isVisible("certificatesSection") && (
          <Box my={4}>
            <h2>
              <T id="certificatesLabel" />
            </h2>
            {sku && sku.id && <SkuCertificatesList skuId={sku.id} />}
          </Box>
        )}

        <h2>
          <T id="propertiesLabel" />
        </h2>
        {isVisible("skuRules") && (
          <Tooltip
            title={
              !sku?.hasChild ? t({ id: "accordionSkuContentRulesDisabled" }) : ""
            }
          >
            <Accordion key="skuContentRulesTitle" disabled={!sku?.hasChild}>
              <AccordionSummary
                sx={styles.accordionSummary}
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography>
                  <T id="skuContentRulesTitle" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SkuRuleSection sku={sku!} readonly={true} />
              </AccordionDetails>
            </Accordion>
          </Tooltip>
        )}

        {propertiesRows.map(({ title, component }) => (
          <Accordion key={title}>
            <AccordionSummary sx={styles} expandIcon={<ExpandMoreIcon />}>
              <Typography>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionDetails}>
              {component}
            </AccordionDetails>
          </Accordion>
        ))}

        <h2>
          <T id="attributes" />
        </h2>

        {isLoadingOrganizationAttributeTypes ? (
          <Skeleton />
        ) : (
          attributesRows.map(({ title, component }) => (
            <Accordion key={title}>
              <AccordionSummary
                sx={styles.accordionSummary}
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography>{title}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={styles.accordionDetails}>
                {component}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </SkuPageLayout>

      <SkuPreview
        skuId={skuId!}
        organizationId={sku?.ownerOrganizationId!}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default SkuFormDetails;
